/**
 * Motor de cálculo de VT e VR.
 *
 * Fórmulas extraídas das células reais do controle (docs/discovery-report.md §5.1):
 *   VT = (Ida + Volta) × multiplicador × dias      — Base!L = [Total Diário] * $I$3 * [dias]
 *   VR = valorDiario × dias                        — Base!R = $R$3 * [dias]
 *
 * `multiplicador` e `valorDiario` NÃO são constantes: vêm de BenefitPolicy, versionada por
 * vigência. Os valores hoje vigentes (1 e 39,00) são apenas o estado atual da política.
 *
 * Toda aritmética usa Decimal. O arquivo atual da Swile traz 9926.060000000027 numa soma
 * que deveria ser 9926.06 — usar float aqui reproduziria o mesmo defeito.
 */
import { Decimal } from 'decimal.js';

// Arredondamento financeiro: metade para cima, 2 casas. Nunca banker's rounding.
Decimal.set({ rounding: Decimal.ROUND_HALF_UP });

export type BenefitKind =
  'VT' | 'VR' | 'MULTIBENEFICIOS' | 'REEMBOLSO_VT' | 'REEMBOLSO_VR' | 'REEMBOLSO_MULTIBENEFICIOS';

export type DecimalInput = Decimal | string | number;

/** Converte para Decimal recusando entrada não finita. */
export function toDecimal(value: DecimalInput): Decimal {
  const decimal = value instanceof Decimal ? value : new Decimal(String(value));
  if (!decimal.isFinite()) throw new Error(`Valor monetário inválido: ${String(value)}`);
  return decimal;
}

/** Arredonda para 2 casas, meia para cima. Aplicado somente no valor final. */
export function money(value: DecimalInput): Decimal {
  return toDecimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

/** Serializa para a string que o Prisma grava em Decimal(12,2). */
export function toMoneyString(value: DecimalInput): string {
  return money(value).toFixed(2);
}

export interface VtPolicy {
  /** Base!$I$3 — hoje 1. */
  multiplier: DecimalInput;
}

export interface VrPolicy {
  /** Base!$R$3 — hoje 39,00. */
  dailyValue: DecimalInput;
}

export interface MultibenefitPolicy {
  /** HOME OFFICE!$G$3 — hoje 150,00. */
  monthlyValue: DecimalInput;
  /** Divisor de dias úteis do mês — hoje 23. */
  monthlyWorkingDays: number;
}

export interface EmployeeFare {
  fareOneWay: DecimalInput;
  fareReturn: DecimalInput;
}

/** Valor diário de transporte: Ida + Volta. */
export function dailyFare(fare: EmployeeFare): Decimal {
  return money(toDecimal(fare.fareOneWay).plus(toDecimal(fare.fareReturn)));
}

/** VT = (Ida + Volta) × multiplicador × dias. */
export function calculateVT(fare: EmployeeFare, days: number, policy: VtPolicy): Decimal {
  assertDays(days);
  const perDay = toDecimal(fare.fareOneWay).plus(toDecimal(fare.fareReturn));
  return money(perDay.times(toDecimal(policy.multiplier)).times(days));
}

/** VR = valorDiário × dias. */
export function calculateVR(days: number, policy: VrPolicy): Decimal {
  assertDays(days);
  return money(toDecimal(policy.dailyValue).times(days));
}

/**
 * Multibenefícios = valorMensal / diasÚteisDoMês × diasHome.
 * Modalidade separada: nunca é somada em VT nem em VR.
 */
export function calculateMultibenefit(homeDays: number, policy: MultibenefitPolicy): Decimal {
  assertDays(homeDays);
  if (policy.monthlyWorkingDays <= 0) {
    throw new Error('Base de dias úteis do mês deve ser maior que zero');
  }
  return money(toDecimal(policy.monthlyValue).dividedBy(policy.monthlyWorkingDays).times(homeDays));
}

/** Dias de home office = base − presenciais − descontos, nunca negativo. */
export function homeOfficeDays(
  monthlyWorkingDays: number,
  presenceDays: number,
  discountDays = 0,
): number {
  const value = monthlyWorkingDays - presenceDays - discountDays;
  return value > 0 ? value : 0;
}

function assertDays(days: number): void {
  if (!Number.isInteger(days) || days < 0) {
    throw new Error(`Quantidade de dias inválida: ${days}`);
  }
}

// ---------------------------------------------------------------------------
// Ajustes aprovados
// ---------------------------------------------------------------------------

export interface ApprovedAdjustment {
  kind: BenefitKind;
  /** Ajuste em dias. Exclusivo com `amount`. */
  days?: number | null;
  /** Ajuste direto em valor. Exclusivo com `days`. */
  amount?: DecimalInput | null;
  status: 'PENDENTE' | 'APROVADO' | 'REJEITADO';
}

export interface AdjustmentTotals {
  days: number;
  amount: Decimal;
}

/**
 * Consolida apenas os ajustes APROVADOS. Pendentes e rejeitados nunca entram no cálculo —
 * é essa a diferença entre o sistema e a coluna D digitada à mão na planilha atual.
 */
export function sumApprovedAdjustments(
  adjustments: readonly ApprovedAdjustment[],
  kind: BenefitKind,
): AdjustmentTotals {
  let days = 0;
  let amount = new Decimal(0);

  for (const adjustment of adjustments) {
    if (adjustment.status !== 'APROVADO' || adjustment.kind !== kind) continue;
    if (adjustment.days !== null && adjustment.days !== undefined) {
      days += adjustment.days;
    }
    if (adjustment.amount !== null && adjustment.amount !== undefined) {
      amount = amount.plus(toDecimal(adjustment.amount));
    }
  }
  return { days, amount: money(amount) };
}

// ---------------------------------------------------------------------------
// Apuração por colaborador
// ---------------------------------------------------------------------------

export interface EmployeeBenefitInput {
  employeeId: string;
  /** Dias distintos com check-in válido, já deduplicados. */
  computedDays: number;
  fare: EmployeeFare;
  /**
   * Valor diário de VT de cada dia apurado (ex.: o do escritório do check-in), um por dia
   * de `computedDays`. Sem ele, todos os dias valem `fare`. Dias de ajuste valem `fare`.
   */
  vtDailyValues?: readonly DecimalInput[];
  /** Valor diário de VR da própria pessoa (Convenia). Sem ele, vale o da política. */
  vrDailyValue?: DecimalInput;
  eligibleVT: boolean;
  eligibleVR: boolean;
  adjustments: readonly ApprovedAdjustment[];
  /** Dias com check-in mas sem escala — informativo, marcado como presença não prevista. */
  unplannedDays?: number;
  /** Dias escalados sem check-in — informativo, NÃO gera pagamento automático. */
  missedDays?: number;
}

export interface BenefitLineResult {
  employeeId: string;
  kind: BenefitKind;
  computedDays: number;
  adjustedDays: number;
  totalDays: number;
  unitValue: Decimal;
  amount: Decimal;
  unplannedDays: number;
  missedDays: number;
}

export interface CalculateOptions {
  vt: VtPolicy;
  vr: VrPolicy;
}

/**
 * Apura VT e VR de um colaborador.
 *
 * Regras aplicadas:
 *  - dias vêm dos check-ins válidos, não da escala;
 *  - ajustes aprovados somam dias (ou valor) com trilha;
 *  - inelegibilidade zera a modalidade, mas a linha continua existindo para conferência;
 *  - total nunca fica negativo.
 */
export function calculateEmployeeBenefits(
  input: EmployeeBenefitInput,
  options: CalculateOptions,
): BenefitLineResult[] {
  const results: BenefitLineResult[] = [];

  const vtAdjust = sumApprovedAdjustments(input.adjustments, 'VT');
  const vrAdjust = sumApprovedAdjustments(input.adjustments, 'VR');

  const vtDays = Math.max(0, input.computedDays + vtAdjust.days);
  const vrDays = Math.max(0, input.computedDays + vrAdjust.days);

  const multiplier = toDecimal(options.vt.multiplier);
  const porDia = input.vtDailyValues;
  let vtUnit = dailyFare(input.fare).times(multiplier);
  let vtBase = calculateVT(input.fare, vtDays, options.vt);
  if (porDia && porDia.length > 0) {
    if (porDia.length !== input.computedDays) {
      throw new Error('vtDailyValues precisa ter um valor por dia apurado.');
    }
    // Cada dia vale o seu próprio valor; os dias de ajuste, o valor de `fare`.
    const somaDias = porDia.reduce<Decimal>((soma, v) => soma.plus(toDecimal(v)), new Decimal(0));
    const ajuste = dailyFare(input.fare).times(vtAdjust.days);
    vtBase = money(Decimal.max(somaDias.plus(ajuste), 0).times(multiplier));
    vtUnit = somaDias.dividedBy(porDia.length).times(multiplier);
  }
  const vtAmount = input.eligibleVT ? money(vtBase.plus(vtAdjust.amount)) : new Decimal(0);

  results.push({
    employeeId: input.employeeId,
    kind: 'VT',
    computedDays: input.computedDays,
    adjustedDays: vtAdjust.days,
    totalDays: input.eligibleVT ? vtDays : 0,
    unitValue: money(vtUnit),
    amount: Decimal.max(vtAmount, 0),
    unplannedDays: input.unplannedDays ?? 0,
    missedDays: input.missedDays ?? 0,
  });

  const vrPolicy: VrPolicy =
    input.vrDailyValue !== undefined ? { dailyValue: input.vrDailyValue } : options.vr;
  const vrAmount = input.eligibleVR
    ? money(calculateVR(vrDays, vrPolicy).plus(vrAdjust.amount))
    : new Decimal(0);

  results.push({
    employeeId: input.employeeId,
    kind: 'VR',
    computedDays: input.computedDays,
    adjustedDays: vrAdjust.days,
    totalDays: input.eligibleVR ? vrDays : 0,
    unitValue: money(vrPolicy.dailyValue),
    amount: Decimal.max(vrAmount, 0),
    unplannedDays: input.unplannedDays ?? 0,
    missedDays: input.missedDays ?? 0,
  });

  return results;
}

/** Soma exata de uma coluna monetária. Existe para a conciliação bloquear divergência. */
export function sumMoney(values: readonly DecimalInput[]): Decimal {
  return money(values.reduce<Decimal>((acc, value) => acc.plus(toDecimal(value)), new Decimal(0)));
}

/** Igualdade monetária ao centavo. Usada na conciliação cálculo × arquivo. */
export function moneyEquals(a: DecimalInput, b: DecimalInput): boolean {
  return money(a).equals(money(b));
}

export { Decimal };
